import { useTosti } from "@/plugins/vuetify-tosti";
import { BaseCrudService } from "@/services/base";
import { floorService } from "@/services/floor";
import { zoneService } from "@/services/zone";
import { Desk, DeskCreateDTO, DeskUpdateDTO, Floor } from "@/swagger/models";

// class DeskService extends BaseCrudService<Desk, DeskCreateDTO, DeskUpdateDTO> {
class DeskService extends BaseCrudService<Desk> {
    tosti = useTosti();

    constructor() {
        super({
            apiPrefix: "desks",
            entityName: "desk",
        });
    }

    build(fields?: Partial<Desk> & Pick<Desk, "zoneId">): Desk {
        return {
            name: "New desk",
            ...fields,
        };
    }

    cleanup(item: Partial<Desk>): Partial<Desk> {
        // @ts-ignore
        if (typeof item.zone === "object") item.zone = item.zone.id;
        return item;
    }

    async getDesksOnFloor(floorId: number, withDeleted: boolean = false) {
        const zones = await zoneService.find({ params: { floor: floorId } });
        const desks: Desk[] = [];

        for (const zone of zones) {
            desks.push(...(await this.find({ params: { zone: zone.id, withDeleted } })));
        }

        return desks;
    }

    async deactivateDesks(deskIds: number[]) {
        let deactivated = 0;
        for (const id of deskIds) {
            await deskService.delete(id, {}, false);
            deactivated++;
        }
        if (deactivated) this.tosti.success({ message: `${deactivated} desk(s) were deactivated` });
    }

    async activateDesks(deskIds: number[]) {
        let activated = 0;
        for (const id of deskIds) {
            await deskService.patch(
                id,
                {
                    // @ts-ignore
                    undelete: true,
                },
                {},
                false,
            );
            activated++;
        }

        if (activated) this.tosti.success({ message: `${activated} desk(s) activated` });
    }

    async generateDesks(zoneId: number) {
        // Ask user for the desk name
        const deskName = prompt(
            "What would you like the first new desk to be called? Please include a number.",
            "Desk #001",
        );
        if (!deskName) return; // User didn't enter anything or pressed cancel

        // Check the validity of the name and extract parts
        const matches = /^([^0-9]*)([0-9]+)([^0-9]*)$/.exec(deskName);
        if (!matches) {
            this.tosti.warning({ message: "Please include a number in the desk name" });
            return;
        }

        // Keep track of stuff
        const prefix = matches[1];
        const suffix = matches[3];
        const minNumberLength = matches[2].length;
        const firstNumber = parseInt(matches[2]);

        // console.log(prefix, suffix, minNumberLength, firstNumber);

        // Ask and verify the amount of desks to be added, ask for confirmation if its 10 or more
        const amount = parseInt(prompt("How many desks would you like to generate?"));
        if (!amount || amount < 0) return;
        if (amount >= 10 && !confirm(`Are you sure you want to generate ${amount} desks?`)) return;

        let successfullyCreatedDesks = 0;

        for (let i = firstNumber; i < firstNumber + amount; i++) {
            const deskId = i.toString();
            const deskIdPadded = "0".repeat(Math.max(0, minNumberLength - deskId.length)) + deskId;
            const deskName = `${prefix}${deskIdPadded}${suffix}`;

            try {
                await this.create({
                    name: deskName,
                    zoneId,
                });
                successfullyCreatedDesks++;
            } catch (e) {
                this.tosti.error({ message: e.message });
            }
        }
        this.tosti.success({ message: `${successfullyCreatedDesks} desks were successfully created` });
    }
}

export const deskService = new DeskService();
